import { useMemo, useState } from 'react';
import { Wallet, Receipt, TrendingUp, RotateCcw, CreditCard, Loader2 } from 'lucide-react';
import type { ListingTransactionPage, TransactionStatus } from '@/client/types/promotion';
import { numberFmt } from './atoms';
import { fmtDate, money, spendByMonth } from './listingMetrics';
import { SpendTrendChart } from './SpendTrendChart';

const TXN_STATUS: Record<TransactionStatus, { label: string; cls: string }> = {
  paid: { label: 'Paid', cls: 'bg-emerald-50 text-emerald-700' },
  refunded: { label: 'Refunded', cls: 'bg-rose-50 text-rose-600' },
  pending: { label: 'Scheduled', cls: 'bg-blue-50 text-blue-600' },
};

const STATUS_FILTERS: { id: 'all' | TransactionStatus; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'refunded', label: 'Refunded' },
  { id: 'pending', label: 'Scheduled' },
];

const ROWS_PER_PAGE = 10;

const SpendTile: React.FC<{ icon: React.ElementType; label: string; value: string; tone: string; sub?: string }> = ({
  icon: Icon,
  label,
  value,
  tone,
  sub,
}) => (
  <div className="rounded-xl border border-gray-100 bg-white px-4 py-3">
    <div className="flex items-center gap-2">
      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="truncate text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    </div>
    <p className="mt-2 text-xl font-semibold tabular-nums text-gray-900">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
  </div>
);

/**
 * `summary` totals cover every transaction for the listing, not just the rows
 * fetched here — the table below holds the newest `data.length` of `total`.
 * The status chips filter those rows client-side on purpose: the endpoint's
 * `status` param also narrows the summary, which would make the tiles read as
 * if the listing had only ever earned the filtered slice.
 */
export const FinancialRecords: React.FC<{
  transactions: ListingTransactionPage | undefined;
  isLoading?: boolean;
  isFetching?: boolean;
}> = ({ transactions, isLoading, isFetching }) => {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<'all' | TransactionStatus>('all');
  const all = transactions?.data ?? [];
  const summary = transactions?.summary;

  const rows = useMemo(
    () => (status === 'all' ? all : all.filter((t) => t.status === status)),
    [all, status],
  );
  const spendRows = useMemo(() => spendByMonth(all), [all]);
  const pageCount = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const visible = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const avg = summary?.paid_count ? Math.round(summary.total_spend / summary.paid_count) : 0;
  const span =
    summary?.first_spend_date && summary?.last_spend_date
      ? `${fmtDate(summary.first_spend_date)} → ${fmtDate(summary.last_spend_date)}`
      : 'No completed purchases';
  const truncated = (transactions?.total ?? 0) > all.length;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">Financial records</h3>
          {isFetching && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
        </div>
        <span className="text-xs text-gray-400">Owner spend on this listing · {span}</span>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SpendTile
          icon={Wallet}
          label="Total spent"
          value={money(summary?.total_spend ?? 0)}
          tone="bg-emerald-50 text-emerald-600"
          sub={`${summary?.paid_count ?? 0} paid ${summary?.paid_count === 1 ? 'purchase' : 'purchases'}`}
        />
        <SpendTile icon={TrendingUp} label="Avg / promotion" value={money(avg)} tone="bg-indigo-50 text-indigo-600" />
        <SpendTile
          icon={RotateCcw}
          label="Refunded"
          value={money(summary?.refunded ?? 0)}
          tone="bg-rose-50 text-rose-600"
          sub={(summary?.refunded ?? 0) > 0 ? 'Includes canceled promos' : 'None'}
        />
        <SpendTile
          icon={Receipt}
          label="Transactions"
          value={numberFmt.format(transactions?.total ?? 0)}
          tone="bg-amber-50 text-amber-600"
        />
      </div>

      <div className="mb-5">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Spend over time</p>
        <SpendTrendChart rows={spendRows} />
        {truncated && (
          <p className="mt-2 text-[11px] text-gray-400">
            Charted from the {numberFmt.format(all.length)} most recent of{' '}
            {numberFmt.format(transactions?.total ?? 0)} transactions.
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-gray-50/60 px-4 py-2.5">
          <Receipt className="h-4 w-4 text-gray-500" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">Transactions</h4>
          <span className="text-xs text-gray-400">{numberFmt.format(transactions?.total ?? 0)}</span>
          <div className="ml-auto inline-flex overflow-hidden rounded-md border border-gray-200">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  setStatus(s.id);
                  setPage(0);
                }}
                className={`px-2.5 py-1 text-[11px] font-medium ${
                  status === s.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[0, 1, 2].map((i) => (
              <div key={i} className="animate-pulse px-4 py-3">
                <div className="h-3 w-56 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            {all.length === 0 ? 'No transactions for this listing.' : 'No transactions with this status.'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                    <th className="px-4 py-2 font-medium">Date</th>
                    <th className="px-4 py-2 font-medium">Promotion</th>
                    <th className="px-4 py-2 font-medium">Method</th>
                    <th className="px-4 py-2 text-right font-medium">Amount</th>
                    <th className="px-4 py-2 text-right font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visible.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">{fmtDate(t.date)}</td>
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-gray-900">{t.promotion_name}</span>
                        <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                          {t.plan}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                        <span className="inline-flex items-center gap-1.5">
                          <CreditCard className="h-3.5 w-3.5 text-gray-400" />
                          {t.method}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">
                        {money(t.amount)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-right">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            (TXN_STATUS[t.status] ?? TXN_STATUS.pending).cls
                          }`}
                        >
                          {(TXN_STATUS[t.status] ?? TXN_STATUS.pending).label}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pageCount > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5 text-xs text-gray-500">
                <span>
                  Page {page + 1} of {pageCount}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="rounded-md border border-gray-200 px-2 py-1 font-medium hover:bg-gray-50 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={page >= pageCount - 1}
                    className="rounded-md border border-gray-200 px-2 py-1 font-medium hover:bg-gray-50 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
